import React, { useState, useEffect, useRef, useCallback } from 'react'
import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  Button,
  LinearProgress,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { courseCreationProgressService } from 'src/api/course'
import URL from 'src/api/url/api'
import styles from './styles'

const slugify = (text) => {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

const LinkCard = ({ classes, label, url, isExternal }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={classes.linkCard}>
      <Typography className={classes.linkLabel}>{label}</Typography>
      <div className={classes.linkContent}>
        <Typography className={classes.linkUrl} title={url}>
          {url}
        </Typography>
        <div className={classes.linkActions}>
          <CopyToClipboard text={url} onCopy={handleCopy}>
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton className={classes.actionIconBtn}>
                <FileCopyIcon fontSize="small" className={copied ? classes.copySuccess : ''} />
              </IconButton>
            </Tooltip>
          </CopyToClipboard>
          <Tooltip title="Open link">
            <IconButton
              className={classes.actionIconBtn}
              component="a"
              href={url}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

const GenerateStep = ({ classes, generateStream, onReset }) => {
  const {
    generating,
    progress,
    step,
    result,
    error,
    reconnecting,
    reconnectAttempt,
  } = generateStream

  const [cardProgress, setCardProgress] = useState(null)
  const [cardsDone, setCardsDone] = useState(false)
  const pollRef = useRef(null)
  const courseProgressRef = useRef({})

  const isMultiCourse = result?.courses && result.courses.length > 0

  const getCourseIds = useCallback(() => {
    if (!result) return []
    if (isMultiCourse) {
      return result.courses.map(c => c.courseId).filter(Boolean)
    }
    return result.courseId ? [result.courseId] : []
  }, [result, isMultiCourse])

  useEffect(() => {
    if (!result) return

    const courseIds = getCourseIds()
    if (courseIds.length === 0) {
      setCardsDone(true)
      return
    }

    courseProgressRef.current = {}
    courseIds.forEach(id => {
      courseProgressRef.current[id] = 0
    })
    setCardProgress({ overall: 0, courses: {} })

    const poll = async () => {
      let allDone = true
      const courseUpdates = {}

      for (const courseId of courseIds) {
        if (courseProgressRef.current[courseId] >= 100) {
          courseUpdates[courseId] = 100
          continue
        }

        try {
          const response = await courseCreationProgressService(courseId)
          const progressData = response.data || response
          const pct = Math.min(progressData.cardCreationProgress || 0, 100)
          courseProgressRef.current[courseId] = pct
          courseUpdates[courseId] = pct
          if (pct < 100) allDone = false
        } catch (e) {
          courseUpdates[courseId] = courseProgressRef.current[courseId] || 0
          allDone = false
        }
      }

      const values = Object.values(courseProgressRef.current)
      const overall = values.length > 0
        ? Math.min(Math.round(values.reduce((a, b) => a + b, 0) / values.length), 100)
        : 0

      setCardProgress({ overall, courses: courseUpdates })

      if (allDone) {
        clearInterval(pollRef.current)
        pollRef.current = null
        setCardsDone(true)
      }
    }

    poll()
    pollRef.current = setInterval(poll, 2000)

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [result, getCourseIds])

  return (
    <div className={classes.root}>
      {/* 1. Content Generation (Generating) */}
      {!result && !error && (
        <div className={classes.progressContainer}>
          <Box mb={4}>
            <CircularProgress size={56} thickness={5} style={{ color: '#3B82F6', marginBottom: '24px' }} />
            <Typography variant="h6" style={{ fontWeight: 700, color: '#0F172A' }}>
              Generating Your Courses
            </Typography>
            <Typography style={{ color: '#64748B', fontSize: '14px', maxWidth: '450px', margin: '8px auto' }}>
              We're now generating the full content for your collection. This includes summaries, questions, and structured lessons based on your PDFs.
            </Typography>
          </Box>

          <Box mt={4} mb={2}>
            <LinearProgress variant="determinate" value={progress} className={classes.progressBar} />
            <Box display="flex" justifyContent="space-between" mt={1}>
              <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#3B82F6' }}>
                {progress}% Complete
              </Typography>
              <Typography style={{ fontSize: '12px', color: '#64748B' }}>
                {reconnecting ? `Reconnecting... (attempt ${reconnectAttempt}/3)` : (step || 'Initializing...')}
              </Typography>
            </Box>
            {reconnecting && (
              <Box mt={1}>
                <Typography variant="caption" style={{ color: '#DC2626', fontWeight: 600 }}>
                  Connection lost. Trying to resume...
                </Typography>
              </Box>
            )}
          </Box>
        </div>
      )}

      {/* 2. Error State */}
      {error && (
        <Box p={4} textAlign="center" style={{ backgroundColor: '#FEF2F2', borderRadius: '16px', border: '1px solid #FECACA' }}>
          <Typography style={{ color: '#B91C1C', fontWeight: 700, fontSize: '18px' }}>
            Generation Failed
          </Typography>
          <Typography style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
            {error}
          </Typography>
          <Box mt={3}>
            <Button
              variant="contained"
              onClick={() => onReset()}
              style={{
                backgroundColor: '#B91C1C',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                padding: '10px 32px',
                borderRadius: '8px',
                boxShadow: 'none',
              }}
            >
              Back to Start
            </Button>
          </Box>
        </Box>
      )}

      {/* 3. Card Creation Progress */}
      {result && !cardsDone && (
        <div className={classes.progressContainer}>
          <Box mb={4}>
            <CircularProgress size={56} thickness={5} style={{ color: '#10B981', marginBottom: '24px' }} />
            <Typography variant="h6" style={{ fontWeight: 700, color: '#0F172A' }}>
              Finalizing Courses
            </Typography>
            <Typography style={{ color: '#64748B', fontSize: '14px', maxWidth: '450px', margin: '8px auto' }}>
              The content is ready! We're now creating the interactive cards for your collection.
            </Typography>
          </Box>

          <Box mt={4} mb={2}>
            <LinearProgress variant="determinate" value={cardProgress?.overall || 0} className={classes.progressBar} />
            <Box display="flex" justifyContent="space-between" mt={1}>
              <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#10B981' }}>
                {cardProgress?.overall || 0}% Finalized
              </Typography>
              <Typography style={{ fontSize: '12px', color: '#64748B' }}>
                Creating course cards...
              </Typography>
            </Box>
          </Box>

          {isMultiCourse && cardProgress?.courses && (
            <div className={classes.courseProgressGrid}>
              {result.courses.map((course, i) => {
                const pct = cardProgress.courses[course.courseId] || 0
                return (
                  <div key={course.courseId} className={classes.courseProgressCard}>
                    <Typography style={{ fontSize: '13px', fontWeight: 700, color: '#1E293B' }}>
                      {course.courseTitle || `Course ${i + 1}`}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      style={{ height: '6px', borderRadius: '3px', backgroundColor: '#F1F5F9' }}
                    />
                    <Typography style={{ fontSize: '11px', color: '#64748B', fontWeight: 500 }}>
                      {pct}% Complete
                    </Typography>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* 4. Final Result */}
      {result && cardsDone && (
        <div className={classes.resultContainer}>
          <div className={classes.resultCard}>
            <CheckCircleIcon style={{ fontSize: '72px', color: '#10B981', marginBottom: '24px' }} />
            <Typography variant="h4" style={{ fontWeight: 800, color: '#1E293B', letterSpacing: '-0.02em' }}>
              Your Learning Path is Ready!
            </Typography>
            <Typography style={{ color: '#64748B', fontSize: '16px', marginTop: '12px', marginBottom: '32px' }}>
              Successfully generated {isMultiCourse ? `${result.courses.length} courses` : 'the course'} from your content.
            </Typography>

            <Box display="flex" flexDirection="column" alignItems="center">
              {isMultiCourse ? (
                (() => {
                  const slug = slugify(result.collectionName || 'collection')
                  const shareUrl = `${URL.APP_URL}category/${slug}-1/${result.collectionId}`
                  const adminUrl = `${window.location.origin}/collection/${result.collectionId}/mini-courses`
                  return (
                    <>
                      <LinkCard classes={classes} label="Admin Link" url={adminUrl} isExternal={false} />
                      <LinkCard classes={classes} label="Share Link" url={shareUrl} isExternal={true} />
                    </>
                  )
                })()
              ) : (
                <LinkCard classes={classes} label="Share Link" url={result.publicUrl} isExternal={true} />
              )}
            </Box>

            <div className={classes.actionButtons}>
              <Button
                variant="contained"
                onClick={onReset}
                style={{
                  backgroundColor: '#3B82F6',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 700,
                  padding: '12px 32px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  boxShadow: 'none',
                }}
              >
                Create New Session
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default withStyles(styles)(GenerateStep)
